/**
 * DepartmentDetailPanel — Slide-out right drawer for viewing / editing
 * an existing department.
 *
 * Features:
 *  - Department name (editable for admins)
 *  - Searchable member list with toggle assignment
 *  - Delete department
 *  - Matches UserDetailPanel slide-in-right pattern
 */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import Building2 from 'lucide-react/dist/esm/icons/building-2';
import Trash2 from 'lucide-react/dist/esm/icons/trash-2';
import UserCheck from 'lucide-react/dist/esm/icons/user-check';
import UserMinus from 'lucide-react/dist/esm/icons/user-minus';
import Users from 'lucide-react/dist/esm/icons/users';
import type { Department, OrgUser } from '../../../services/api/org';
import { Button, IconButton, SearchInput, Text, SlidePanel } from '../../../components/ui';

interface DepartmentDetailPanelProps {
  department: Department | null;
  isOpen: boolean;
  onClose: () => void;
  isAdmin: boolean;
  users: OrgUser[];
  onSave: (name: string, memberIds: string[]) => Promise<void>;
  onDelete?: () => void;
}

const DepartmentDetailPanel: React.FC<DepartmentDetailPanelProps> = ({
  department,
  isOpen,
  onClose,
  isAdmin,
  users,
  onSave,
  onDelete,
}) => {
  const nameRef = useRef<HTMLInputElement>(null);

  const [name, setName] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Reset state when panel opens / department changes
  useEffect(() => {
    if (isOpen && department) {
      setName(department.name);
      setSelectedIds(
        new Set(
          users
            .filter((u) => u.department_id === department.id)
            .map((u) => u.id)
        )
      );
      setSearch('');
      setError(null);
      setSaving(false);
      setDirty(false);
      setShowDeleteConfirm(false);
    }
  }, [isOpen, department, users]);

  // Filter users by search
  const filteredUsers = useMemo(() => {
    const q = search.toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) =>
        (u.display_name || '').toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q)
    );
  }, [users, search]);

  // Sort: assigned first, then alphabetical
  const sortedUsers = useMemo(() => {
    return [...filteredUsers].sort((a, b) => {
      const aSelected = selectedIds.has(a.id) ? 0 : 1;
      const bSelected = selectedIds.has(b.id) ? 0 : 1;
      if (aSelected !== bSelected) return aSelected - bSelected;
      return (a.display_name || a.email).localeCompare(b.display_name || b.email);
    });
  }, [filteredUsers, selectedIds]);

  const toggleUser = (userId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
    setDirty(true);
  };

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Department name is required');
      nameRef.current?.focus();
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(trimmed, Array.from(selectedIds));
      setDirty(false);
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to save department');
    } finally {
      setSaving(false);
    }
  };

  if (!department) return null;

  const memberCount = selectedIds.size;

  return (
    <SlidePanel isOpen={isOpen} onClose={onClose} width="max-w-[460px]">
      {/* Custom header */}
      <div className="flex items-center gap-4 px-6 py-5 border-b border-subtle">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-brand/30 to-brand/10 flex items-center justify-center shrink-0">
          <Building2 size={20} className="text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          {isAdmin ? (
            <>
              <input
                ref={nameRef}
                type="text"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  setDirty(true);
                  if (error) setError(null);
                }}
                className="w-full bg-transparent text-base font-semibold text-content outline-none border-b border-transparent focus:border-brand/40 transition-colors pb-0.5"
                placeholder="Department name"
              />
              {error && (
                <p className="text-2xs text-status-error mt-1">{error}</p>
              )}
            </>
          ) : (
            <h3 className="text-base font-semibold text-content truncate">
              {department.name}
            </h3>
          )}
          <div className="flex items-center gap-2 mt-1">
            <Users size={11} className="text-content-faint shrink-0" />
            <span className="text-xs text-content-muted">
              {memberCount} member{memberCount !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
      </div>

      {/* Members Section */}
      <div className="flex-1 overflow-y-auto px-6 py-5 min-h-0 flex flex-col">
        <div className="flex items-center justify-between mb-3">
          <Text variant="label">Members</Text>
          <span className="text-2xs text-content-muted">
            {selectedIds.size} assigned
          </span>
        </div>

        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search people..."
          onClear={() => setSearch('')}
          className="mb-3"
        />

        {/* User List */}
        <div className="space-y-1 flex-1 overflow-y-auto pr-1 -mr-1">
          {sortedUsers.map((user) => {
            const isSelected = selectedIds.has(user.id);
            return (
              <button
                key={user.id}
                type="button"
                onClick={() => isAdmin && toggleUser(user.id)}
                className={`
                  w-full flex items-center gap-3 p-2.5 rounded-xl text-left transition-all border outline-none
                  ${isAdmin ? 'cursor-pointer' : 'cursor-default'}
                  ${isSelected
                    ? 'bg-brand/10 border-brand/20 hover:bg-brand/15'
                    : isAdmin
                      ? 'bg-transparent border-transparent hover:bg-surface-primary hover:border-subtle'
                      : 'bg-transparent border-transparent'
                  }
                `}
              >
                {/* Avatar */}
                <div className={`
                  w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 transition-colors
                  ${isSelected
                    ? 'bg-brand/30 text-primary'
                    : 'bg-surface-secondary text-content-muted'
                  }
                `}>
                  {(user.display_name || user.email)[0]?.toUpperCase()}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-content truncate">
                    {user.display_name || user.email.split('@')[0]}
                  </div>
                  <div className="text-[10px] text-content-muted truncate">
                    {user.email}
                  </div>
                </div>

                {/* Toggle icon */}
                {isSelected ? (
                  <UserCheck size={14} className="text-primary shrink-0" />
                ) : (
                  <UserMinus size={14} className="text-content-faint shrink-0" />
                )}
              </button>
            );
          })}

          {sortedUsers.length === 0 && (
            <div className="text-center py-6">
              <p className="text-xs text-content-muted">
                {search ? 'No matching people' : 'No people in the organization yet'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      {isAdmin && (
        <div className="px-6 py-4 border-t border-subtle flex items-center gap-3">
          {/* Delete */}
          {onDelete && (
            <>
              {showDeleteConfirm ? (
                <div className="flex items-center gap-2 mr-auto">
                  <span className="text-xs text-status-error">Delete this department?</span>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => {
                      onDelete();
                      onClose();
                    }}
                  >
                    Yes, delete
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowDeleteConfirm(false)}
                  >
                    No
                  </Button>
                </div>
              ) : (
                <IconButton
                  icon={<Trash2 size={16} />}
                  variant="danger"
                  aria-label="Delete department"
                  onClick={() => setShowDeleteConfirm(true)}
                  className="mr-auto"
                />
              )}
            </>
          )}

          {!onDelete && <div className="flex-1" />}

          <Button variant="ghost" size="lg" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="lg"
            loading={saving}
            disabled={!dirty}
            onClick={handleSave}
          >
            Save Changes
          </Button>
        </div>
      )}
    </SlidePanel>
  );
};

export default React.memo(DepartmentDetailPanel);
